import React, { useState } from 'react';
import { Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface MultiItemWarningBannerProps {
  /**
   * Count of distinct product types Stage 1 detected in the customer's raw
   * requirement text. The banner only renders when this is greater than 1
   * (i.e., the request describes more than one distinct product). Sourced
   * from Stage 1's `num_items_detected` field, which is propagated through
   * stage{1,2,3}-ui.json outputs and the
   * `POST /api/requirements/questions` response body.
   *
   * Defaults to undefined; treat as "single product" / no banner.
   */
  numItemsDetected?: number;
}

/**
 * Amber/yellow warning banner shown when Stage 1 detected multiple distinct
 * products in the customer's requirement text. Renders verbatim:
 *
 *   "The AI has detected multiple products in this workflow.
 *    For better results, include only 1 product per workflow."
 *
 * Dismissal semantics: the spec asks for "dismissible per session" while
 * also stating the banner should re-appear on page reload. Those are
 * contradictory if "session" means the browser session (sessionStorage
 * survives reload). We resolve this by storing the dismissal in
 * component-local React state only — dismissal lasts for the current SPA
 * navigation and is cleared on full page reload (or component remount).
 */
const MultiItemWarningBanner: React.FC<MultiItemWarningBannerProps> = ({
  numItemsDetected,
}) => {
  const [dismissed, setDismissed] = useState(false);

  const shouldRender =
    typeof numItemsDetected === 'number' && numItemsDetected > 1;

  if (!shouldRender || dismissed) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={
        <IconButton
          aria-label="Close warning"
          color="inherit"
          size="small"
          onClick={() => setDismissed(true)}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
    >
      The AI has detected multiple products in this workflow. For better
      results, include only 1 product per workflow.
    </Alert>
  );
};

export default MultiItemWarningBanner;
